import { Button, Form, Input, message, Modal, Typography } from 'antd'
import React, { useState } from 'react'
import * as com from '../Common.js'
import './ContinueJourney.css'

const { Text } = Typography

const ContinueJourney = ({ isOpen, anonymousId, onClose, onSuccess }) => {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [formValues, setFormValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({}) // Track field error states

    // Phone number change handler
    const onChangePhone = (e) => {
        const phone = com.fixPhoneInput(e.target.value)
        form.setFieldsValue({
            phone: phone,
        })
    }

    // Custom clear icon for error states
    const customClearIcon = (
        <div className="custom-clear-icon">
            <svg
                width="7.78"
                height="7.78"
                viewBox="0 0 7.78 7.78"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M5.835 1.945L1.945 5.835M1.945 1.945L5.835 5.835"
                    stroke="white"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    )

    // Handle field validation changes to track error states
    const handleFieldsChange = (changedFields, allFields) => {
        const errors = {}
        allFields.forEach((field) => {
            if (field.errors && field.errors.length > 0) {
                errors[field.name[0]] = true
            } else {
                errors[field.name[0]] = false
            }
        })
        setFieldErrors(errors)
    }

    // Check if all required fields are filled
    const isFormValid =
        formValues.firstName &&
        formValues.lastName &&
        formValues.email &&
        formValues.phone

    const handleSubmit = async (values) => {
        setLoading(true)
        try {
            const response = await fetch('/los/mortgagegpt/submitanonymous', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization:
                        'Bearer ' + sessionStorage.getItem('ZeitroA'),
                    // Send anonymous ID in header for backend tracking
                    'X-Anonymous-ID': anonymousId,
                },
                body: JSON.stringify({
                    FirstName: values.firstName,
                    LastName: values.lastName,
                    Email: values.email,
                    Phone: values.phone,
                    CompanyName: values.company || '',
                    LeadSource: 'GPT guideline',
                    IndividualNMLS: '',
                    BrokerageNMLS: '',
                    Password: '',
                }),
            })

            if (response.ok) {
                message.success('Information submitted successfully!')
                form.resetFields()
                onClose()
                if (onSuccess) {
                    onSuccess()
                }
            } else {
                throw new Error('Failed to submit information')
            }
        } catch (error) {
            console.error('Submit error:', error)
            message.error('Failed to submit information. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={580}
            centered
            // closable={false}
            // maskClosable={false}
            className="continue-journey-modal"
        >
            <div className="continue-journey-content">
                {/* Header */}
                <div className="continue-journey-header">
                    <Text className="continue-journey-title">
                        Continue your journey
                    </Text>
                    <Text className="continue-journey-subtitle">
                        To keep using MortgageGPT and get the most helpful
                        responses, we'd like to know a bit about you.
                    </Text>
                </div>

                {/* Benefits Section */}
                <div className="continue-journey-benefits">
                    <div className="benefits-title">This helps us</div>
                    <div className="benefits-list">
                        <div className="benefit-item">
                            <div className="benefit-icon">
                                <svg
                                    width="14"
                                    height="10"
                                    viewBox="0 0 14 10"
                                    fill="none"
                                >
                                    <path
                                        d="M1 5L5 9L13 1"
                                        stroke="#325CEB"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <span className="benefit-text">
                                Provide more personalized answer
                            </span>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">
                                <svg
                                    width="14"
                                    height="10"
                                    viewBox="0 0 14 10"
                                    fill="none"
                                >
                                    <path
                                        d="M1 5L5 9L13 1"
                                        stroke="#325CEB"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <span className="benefit-text">
                                Continue with 5 free chats a day
                            </span>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">
                                <svg
                                    width="14"
                                    height="10"
                                    viewBox="0 0 14 10"
                                    fill="none"
                                >
                                    <path
                                        d="M1 5L5 9L13 1"
                                        stroke="#325CEB"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <span className="benefit-text">
                                Always access the latest guidelines
                            </span>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    className="continue-journey-form"
                    requiredMark={false}
                    validateTrigger="onBlur"
                    onValuesChange={(changedValues, allValues) => {
                        setFormValues(allValues)
                    }}
                    onFieldsChange={handleFieldsChange}
                >
                    <div className="form-row">
                        <Form.Item
                            name="firstName"
                            label="First name*"
                            rules={[
                                {
                                    required: true,
                                    message: 'This field is required.',
                                    validateTrigger: 'onSubmit',
                                },
                            ]}
                            className="form-item-half"
                            validateTrigger={['onBlur', 'onChange']}
                        >
                            <Input
                                placeholder="Required"
                                autoComplete="off"
                                allowClear={{
                                    clearIcon: customClearIcon,
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="lastName"
                            label="Last name*"
                            rules={[
                                {
                                    required: true,
                                    message: 'This field is required.',
                                    validateTrigger: 'onSubmit',
                                },
                            ]}
                            className="form-item-half"
                            validateTrigger={['onBlur', 'onChange']}
                        >
                            <Input
                                placeholder="Required"
                                autoComplete="off"
                                allowClear={{
                                    clearIcon: customClearIcon,
                                }}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="email"
                        label="Email*"
                        rules={[
                            {
                                required: true,
                                type: 'email',
                                message: 'This field is required.',
                                validateTrigger: 'onSubmit',
                            },
                            {
                                type: 'email',
                                message: 'Please enter a valid email address.',
                                validateTrigger: 'onBlur',
                            },
                        ]}
                        validateTrigger={['onBlur', 'onChange']}
                    >
                        <Input
                            placeholder="Required"
                            type="email"
                            autoComplete="off"
                            allowClear={{
                                clearIcon: customClearIcon,
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label="Phone number*"
                        normalize={(value) => {
                            return com.fixPhoneInput(value)
                        }}
                        rules={[
                            {
                                required: true,
                                message: 'This field is required.',
                                validateTrigger: 'onSubmit',
                            },
                            {
                                pattern:com.phoneRegex,
                                message: com.phoneRegexMessage,
                                validateTrigger: 'onBlur',
                            },
                        ]}
                        validateTrigger={['onBlur', 'onChange']}
                    >
                        <Input
                            placeholder="Required"
                            onChange={onChangePhone}
                            maxLength={14}
                            autoComplete="off"
                            allowClear={{
                                clearIcon: customClearIcon,
                            }}
                        />
                    </Form.Item>

                    <Form.Item name="company" label="Company / Brokerage">
                        <Input
                            placeholder="Optional"
                            autoComplete="off"
                            allowClear={false}
                        />
                    </Form.Item>

                    <Form.Item className="submit-button-item">
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            disabled={!isFormValid}
                            block
                            size="large"
                            className={`continue-journey-submit-btn ${
                                !isFormValid ? 'disabled' : ''
                            }`}
                        >
                            Continue using MortgageGPT
                        </Button>
                    </Form.Item>

                    <div className="privacy-notice">
                        By clicking the button, you agree to share your
                        information.
                    </div>
                </Form>

                {/* Privacy Policy Link */}
                <div className="privacy-links">
                    <a
                        href="https://www.zeitro.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Privacy Policy
                    </a>
                </div>
            </div>
        </Modal>
    )
}

export default ContinueJourney
